<template>
    <rkm-modal
        title="Novo paciente"
        ref="Modal"
        extraLarge
    >

        <rkm-form
            ref="Form"
            show-legend
        >

            <rkm-area
                title="Paciente"
            >
                <rkm-row>
                    <rkm-col>
                        <rkm-form-input-autocomplete
                            label="Nome"
                            v-model="model.paciente"
                            :repository="repositories.paciente"
                            :search-fields="autocomplete.paciente"
                            :text-field="(item) =>  item.cidadao.cns ? `${this.formatCns(item.cidadao.cns)} - ${item.cidadao.nome}` : item.cidadao.nome"
                            :validations="validations.paciente"
                            :disabled="model.exportacao"
                            uppercase
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            label="Nome social"
                            v-model="nomeSocial"
                            disabled
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            label="CNS"
                            v-model="cns"
                            disabled
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            label="Data de nascimento"
                            v-model="dataNascimento"
                            disabled
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-input
                            label="Área/Microárea"
                            v-model="area"
                            disabled
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            label="Local de atendimento"
                            :options="form.locaisAtendimento"
                            v-model="model.localAtendimentoId"
                            :validations="validations.localAtendimento"
                            value-field="id"
                            text-field="descricao"
                            show-default-option
                            :disabled="model.exportacao"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            label="Tipo de atendimento"
                            :options="form.tiposAtendimento"
                            v-model="model.tipoAtendimentoId"
                            :validations="validations.tipoAtendimento"
                            value-field="id"
                            text-field="descricao"
                            show-default-option
                            :disabled="model.exportacao"
                            @change="handleTipoAtendimento()"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-select
                            label="Modalidade"
                            :options="form.modalidades"
                            v-model="model.modalidadeId"
                            :validations="validations.modalidade"
                            value-field="id"
                            text-field="descricao"
                            show-default-option
                            :disabled="disableModalidade"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            label="Turno"
                            :options="form.turnos"
                            v-model="model.turnoId"
                            :validations="validations.turno"
                            value-field="id"
                            text-field="nome"
                            show-default-option
                            :disabled="model.exportacao"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-area>

            <rkm-area
                title="Condições avaliadas"
            >
                <rkm-row>
                    <rkm-col>
                        <rkm-form-checkbox-group
                            v-model="model.condicoes"
                            :disabled="disableCondicoesAvaliadas"
                            :validations="{}"
                        >
                            <rkm-form-checkbox
                                v-for="condicao in form.condicoes"
                                :key="condicao.codigo"
                                :value="condicao.codigo"
                                class="w-50 m-0"
                            >
                                {{ condicao.nome }}
                            </rkm-form-checkbox>
                        </rkm-form-checkbox-group>
                    </rkm-col>
                </rkm-row>
            </rkm-area>

            <rkm-area
                v-if="model.paciente"
                type="fieldset"
                title="Problemas e/ou condições avaliados *"
            >
                <FormProblemaCondicao
                    :data="{ form, model }"
                />
            </rkm-area>

            <rkm-area
                title="Procedimentos"
            >
                <rkm-row>
                    <rkm-col>
                        <rkm-form-checkbox-group
                            v-model="model.procedimentos"
                            :disabled="disableProcedimentos"
                        >
                            <rkm-form-checkbox
                                v-for="procedimento in form.procedimentos"
                                :key="procedimento.codigo"
                                :value="procedimento"
                                class="w-50 m-0"
                            >
                                {{ procedimento.nome }}
                            </rkm-form-checkbox>
                        </rkm-form-checkbox-group>
                    </rkm-col>
                </rkm-row>
            </rkm-area>

            <rkm-area
                title="Outros procedimentos"
            >
                <rkm-row>
                    <rkm-col cols="11">
                        <rkm-form-input-autocomplete
                            name="Outros procedimentos"
                            v-model="model.procedimento"
                            :repository="repositories.procedimento"
                            :request-query-string-params="autocompleteRequestParams.procedimento.queryString"
                            :search-fields="fields.autocomplete.procedimento"
                            :text-field="p => `${this.formatProcedimento(p.codigo)} - ${p.nome}`"
                            :disabled="disableProcedimentos"
                        />
                    </rkm-col>

                    <rkm-col cols="1">
                        <rkm-button
                            @click="incluirProcedimento()"
                            :disabled="disableProcedimentos"
                            color="primary"
                        >Incluir</rkm-button>
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-table
                            hide-filters
                            hide-pagination
                            no-footer-sorting
                            :items="procedimentos"
                            :fields="tableFields"
                        >
                            <template v-slot:cell(actions)="{ index }">
                                <rkm-button
                                    @click="handleDestroy(index)"
                                    color="danger"
                                    title="Excluir"
                                    :disabled="model.exportacao"
                                    small
                                >
                                    <rkm-icon icon="trash"/>
                                </rkm-button>
                            </template>
                        </rkm-table>
                    </rkm-col>
                </rkm-row>
            </rkm-area>

            <rkm-area
                title="Outros"
            >
                <rkm-form-select
                    label="Conduta/Desfecho"
                    :options="form.condutaDesfecho"
                    v-model="model.condutaDesfechoId"
                    :validations="validations.condutaDesfecho"
                    value-field="id"
                    text-field="descricao"
                    show-default-option
                    :disabled="model.exportacao"
                />
            </rkm-area>
        </rkm-form>

        <template v-slot:footer="{ cancel }">
            <rkm-button @click="cancel"> Fechar </rkm-button>

            <rkm-button
                color="primary"
                @click="savePaciente"
                :disabled="model.exportacao"
            >
                Adicionar
            </rkm-button>
        </template>
    </rkm-modal>
</template>

<script>
import tipoAtendimento from '@/enums/atencao-basica/atendimento-domiciliar/tipoAtendimento';
import procedimento from '@/enums/atencao-basica/atendimento-domiciliar/procedimento';
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import FormProblemaCondicao from './problema-condicao/Index.vue'
import { repository, error, formats, stringCase }  from '@/mixins';
import sexo from '@/enums/cadastro/cidadao/sexo'
import { mapActions } from "vuex";

export default {
    components: {
        FormProblemaCondicao,
    },
    created() {
        this.repositories = {
            atendimentoDomiciliar: this.getConectaSusRepository('atencaoBasica.atendimentoDomiciliar'),
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
            procedimento: this.getConectaSusRepository('sigtap.procedimento'),
            ciap: this.getConectaSusRepository('geral.ciap'),
            cid: this.getConectaSusRepository('sigtap.cid'),
        }
    },
    data: () => ({
        index: null,
        atendimentoId: null,
        enums: {
            sexo,
            tipoAtendimento,
            procedimento,
            subclassificacao
        },
    }),
    computed: {
        procedimentos() {
            return this.model.procedimentos.filter(procedimento => {
                return ![
                    this.enums.procedimento.VISITA_DOMICILIAR_POS_OBITO,
                    this.enums.procedimento.COLETA_MATERIAL_EXAME_LABORATORIAL,
                    this.enums.procedimento.ANTIBIOTICOTERAPIA_PARENTERAL,
                    this.enums.procedimento.ATENDIMENTO_MEDICO_FINALIDADE_ATESTAR_OBITO,
                    this.enums.procedimento.TERAPIA_REIDRATACAO_PARENTERAL,
                    this.enums.procedimento.ACOMPANHAMENTO_PACIENTE_REABILITACAO_COMUNICACAO_ALTERNATIVA,
                    this.enums.procedimento.ATENDIMENTO_ACOMPANHAMENTO_REABILITACAO_MULTIPLAS_DEFICIENCIAS,
                    this.enums.procedimento.ATENDIMENTO_ACOMPANHAMENTO_PACIENTE_REABILITACAO_DESENVOLVIMENTO_NEUROPSICOMOTOR,
                    this.enums.procedimento.TERAPIA_FONOAUDIOLOGICA_INDIVIDUAL,
                    this.enums.procedimento.CATETERISMO_VESICAL_ALIVIO,
                    this.enums.procedimento.CATETERISMO_VESICAL_DEMORA,
                    this.enums.procedimento.CUIDADOS_ESTOMAS,
                    this.enums.procedimento.CUIDADOS_TRAQUEOSTOMIA,
                    this.enums.procedimento.ENEMA,
                    this.enums.procedimento.OXIGENOTERAPIA_DIA,
                    this.enums.procedimento.RETIRADA_PONTOS_CIRURGIAS,
                    this.enums.procedimento.SONDAGEM_GASTRICA,
                    this.enums.procedimento.TERAPIA_DE_REIDRATACAO_ORAL,
                    this.enums.procedimento.ATENDIMENTO_FISIOTERAPEUTICO_PACIENTE_TRANSTORNO_RESPIRATORIO_SEM_COMPLICACOES_SISTEMICAS,
                    this.enums.procedimento.TRATAMENTO_EM_REABILITACAO,
                    this.enums.procedimento.TRATAMENTO_DE_TRAUMATISMOS_DE_LOCALIZACAO_ESPECIFICADA_NAO_ESPECIFICADA,
                    this.enums.procedimento.TRAQUEOSTOMIA,
                ].includes(procedimento.codigo)
            })
        },
        disableModalidade() {
            return this.model.tipoAtendimentoId == this.enums.tipoAtendimento.VISITA_DOMICILIAR_POS_OBITO || this.model.exportacao
        },
        disableCondicoesAvaliadas() {
            return this.model.tipoAtendimentoId == this.enums.tipoAtendimento.VISITA_DOMICILIAR_POS_OBITO || this.model.exportacao
        },
        disableProcedimentos() {
            return this.model.tipoAtendimentoId == this.enums.tipoAtendimento.VISITA_DOMICILIAR_POS_OBITO || this.model.procedimentos.length >= 30 || this.model.exportacao
        },
        autocompleteRequestParams() {

            const ocupacao = this.form.ocupacoes.find(u => u.id === this.model.ocupacaoId) || {};

            return {
                procedimento: {
                    queryString: {
                        search_by_apelido: this.model.searchByApelido,
                        paciente_id: this.model.paciente
                            ? this.model.paciente.id
                            : null,
                        cbo: ocupacao.codigo
                            ? ocupacao.codigo
                            : null,
                        only_with_cbo: true,
                        atendimento_domiciliar: true,
                        where_not: {
                            'codigo': [
                                this.enums.procedimento.VISITA_DOMICILIAR_POS_OBITO,
                                this.enums.procedimento.COLETA_MATERIAL_EXAME_LABORATORIAL,
                                this.enums.procedimento.ANTIBIOTICOTERAPIA_PARENTERAL,
                                this.enums.procedimento.ATENDIMENTO_MEDICO_FINALIDADE_ATESTAR_OBITO,
                                this.enums.procedimento.TERAPIA_REIDRATACAO_PARENTERAL,
                                this.enums.procedimento.ACOMPANHAMENTO_PACIENTE_REABILITACAO_COMUNICACAO_ALTERNATIVA,
                                this.enums.procedimento.ATENDIMENTO_ACOMPANHAMENTO_REABILITACAO_MULTIPLAS_DEFICIENCIAS,
                                this.enums.procedimento.ATENDIMENTO_ACOMPANHAMENTO_PACIENTE_REABILITACAO_DESENVOLVIMENTO_NEUROPSICOMOTOR,
                                this.enums.procedimento.TERAPIA_FONOAUDIOLOGICA_INDIVIDUAL,
                                this.enums.procedimento.CATETERISMO_VESICAL_ALIVIO,
                                this.enums.procedimento.CATETERISMO_VESICAL_DEMORA,
                                this.enums.procedimento.CUIDADOS_ESTOMAS,
                                this.enums.procedimento.CUIDADOS_TRAQUEOSTOMIA,
                                this.enums.procedimento.ENEMA,
                                this.enums.procedimento.OXIGENOTERAPIA_DIA,
                                this.enums.procedimento.RETIRADA_PONTOS_CIRURGIAS,
                                this.enums.procedimento.SONDAGEM_GASTRICA,
                                this.enums.procedimento.TERAPIA_DE_REIDRATACAO_ORAL,
                                this.enums.procedimento.ATENDIMENTO_FISIOTERAPEUTICO_PACIENTE_TRANSTORNO_RESPIRATORIO_SEM_COMPLICACOES_SISTEMICAS,
                                this.enums.procedimento.TRATAMENTO_EM_REABILITACAO,
                                this.enums.procedimento.TRATAMENTO_DE_TRAUMATISMOS_DE_LOCALIZACAO_ESPECIFICADA_NAO_ESPECIFICADA,
                                this.enums.procedimento.TRAQUEOSTOMIA,
                            ]
                        }
                    }
                },
                cid: {
                    sexo: this.model.paciente?.cidadao?.sexoId
                    ? this.model.paciente.cidadao.sexoId == this.enums.sexo.MASCULINO ? 'M' : 'F'
                    : null,
                },
                ciap: {
                    sexo_id: this.model.paciente?.cidadao?.sexoId,
                },
            }
        },
        fields() {
            return {
                autocomplete: {
                    procedimento: [
                        { key: "nome", searchable: true },
                        { key: "codigo", searchable: true },
                    ],
                    cid: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    ciap: [
                        { key: 'codigo', searchable: true },
                        { key: 'descricao', searchable: true },
                    ],
                }
            }
        },
        tableFields() {
            return [
                {
                    key: "nome",
                    label: "Procedimento",
                    fixed: true
                },
                {
                    name: 'actions',
                    key: 'actions',
                    label: 'Ações',
                    class: 'col-min'
                },
            ]
        },
        validations() {
            return {
                paciente: {
                    required: true
                },
                localAtendimento: {
                    required: true
                },
                tipoAtendimento: {
                    required: true
                },
                modalidade: {
                    required: true
                },
                turno: {
                    required: true
                },
                condutaDesfecho: {
                    required: true
                }
            }
        },
        autocomplete() {
            return {
                paciente: [
                    { key: 'cidadao.nome', searchable: true },
                ],
            }
        },
        nomeSocial() {
            return this.model.paciente?.cidadao?.nomeSocial ?? null
        },
        cns() {
            return this.model.paciente?.cidadao?.cns ? this.formatCns(this.model.paciente?.cidadao.cns) : null
        },
        dataNascimento() {
            return this.model.paciente?.cidadao.dataNascimento ? this.formatDate(this.model.paciente?.cidadao.dataNascimento) : null
        },
        area() {
            return this.model.area
        }
    },
    methods:{
        ...mapActions(["setLoading", "unsetLoading"]),
        getArea() {
            this.setLoading()
            this.repositories.atendimentoDomiciliar.getArea({
                paciente_id: this.model.paciente.id
            })
                .then(({ data }) => {
                    this.model.area = data
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        open(index = null) {
            if(index === null) {
                this.clearForm()
            } else {
                this.index = index
                const atendimento = this.model.atendimentos[index]
                this.atendimentoId = atendimento.id
                this.model.paciente = {
                    id: atendimento.pacienteId ?? atendimento.paciente?.id,
                    cidadao: {
                        nome: atendimento.pacienteNome ?? atendimento.paciente?.cidadao.nome,
                        nomeSocial: atendimento.pacienteNomeSocial ?? atendimento.paciente?.cidadao.nomeSocial,
                        cns: atendimento.cns ?? atendimento.paciente?.cidadao.cns,
                        dataNascimento: atendimento.dataNascimento ?? atendimento.paciente?.cidadao.dataNascimento
                    }
                }
                this.model.localAtendimentoId = atendimento.localAtendimentoId
                this.model.tipoAtendimentoId = atendimento.tipoAtendimentoId
                this.model.modalidadeId = atendimento.modalidadeId
                this.model.turnoId = atendimento.turnoId
                this.model.condicoes = atendimento.condicoes
                this.model.cid = atendimento.codigoCid
                this.model.ciap = atendimento.codigoCiap
                this.model.procedimentos = atendimento.procedimentos
                this.model.condutaDesfechoId = atendimento.condutaDesfechoId
                this.model.condicaoAvaliada = atendimento.condicaoAvaliada
                this.model.doencaTransmissivel = atendimento.doencaTransmissivel
                this.model.rastreamento = atendimento.rastreamento
                this.model.problemasCondicoes = atendimento.problemasCondicoes
            }
            this.$refs.Modal.show()
        },
        clearForm() {
            this.index = null
            this.model.paciente = null
            this.model.localAtendimentoId = null
            this.model.tipoAtendimentoId = null
            this.model.modalidadeId = null
            this.model.turnoId = null
            this.model.condicoes = []
            this.model.cid = null
            this.model.ciap = null
            this.model.procedimentos = []
            this.model.condutaDesfechoId = null
            this.model.condicaoAvaliada = []
            this.model.doencaTransmissivel = []
            this.model.rastreamento = []
            this.model.problemasCondicoes = []
        },
        incluirProcedimento() {
            if(this.model.procedimento && !this.model.procedimentos.find(procedimento => (procedimento.codigo == this.model.procedimento.codigo))) {
                this.model.procedimentos.push({
                    id: this.model.procedimento.id,
                    nome: this.model.procedimento.nome,
                    codigo: this.model.procedimento.codigo
                })
                this.model.procedimento = null
            }
        },
        handleDestroy(index) {
            this.model.procedimentos.splice(index, 1)
        },
        savePaciente() {

            this.$refs.Form.validate()
                .then(valid => {
                    if(valid) {

                        if(this.index !== null) {
                            this.model.atendimentos.splice(this.index, 1, {
                                id: this.atendimentoId,
                                pacienteId: this.model.paciente?.id,
                                pacienteNome: this.model.paciente?.cidadao?.nome,
                                pacienteNomeSocial: this.model.paciente?.cidadao?.nomeSocial,
                                cns: this.cns,
                                dataNascimento: this.dataNascimento,
                                localAtendimentoId: this.model.localAtendimentoId,
                                tipoAtendimentoId: this.model.tipoAtendimentoId,
                                modalidadeId: this.model.modalidadeId,
                                turnoId: this.model.turnoId,
                                condicoes: this.model.condicoes,
                                cid: this.model.cid?.codigo,
                                ciap: this.model.ciap?.codigo,
                                procedimentos: this.model.procedimentos,
                                condutaDesfechoId: this.model.condutaDesfechoId,
                                condicaoAvaliada: this.model.condicaoAvaliada,
                                doencaTransmissivel: this.model.doencaTransmissivel,
                                rastreamento: this.model.rastreamento,
                                problemasCondicoes: this.model.problemasCondicoes,
                            })
                        }
                        else {
                            this.model.atendimentos.push({
                                id: null,
                                pacienteId: this.model.paciente?.id,
                                pacienteNome: this.model.paciente?.cidadao?.nome,
                                cns: this.cns,
                                dataNascimento: this.dataNascimento,
                                localAtendimentoId: this.model.localAtendimentoId,
                                tipoAtendimentoId: this.model.tipoAtendimentoId,
                                modalidadeId: this.model.modalidadeId,
                                turnoId: this.model.turnoId,
                                condicoes: this.model.condicoes,
                                cid: this.model.cid?.codigo,
                                ciap: this.model.ciap?.codigo,
                                procedimentos: this.model.procedimentos,
                                condutaDesfechoId: this.model.condutaDesfechoId,
                                condicaoAvaliada: this.model.condicaoAvaliada,
                                doencaTransmissivel: this.model.doencaTransmissivel,
                                rastreamento: this.model.rastreamento,
                                problemasCondicoes: this.model.problemasCondicoes,
                            })
                        }
                        this.$refs.Modal.hide()
                    }
                })
        },
        handleTipoAtendimento() {

            if (this.model.tipoAtendimentoId == this.enums.tipoAtendimento.VISITA_DOMICILIAR_POS_OBITO) {

                this.model.condicoes = []
                this.model.procedimentos = []
                this.model.condicaoAvaliada = []
                this.model.doencaTransmissivel = []
                this.model.rastreamento = []
                this.model.problemasCondicoes = []
            }
        }
    },
    watch: {
        'model.paciente': function() {

            if(this.model.paciente) {

                this.setLoading()

                this.repositories.paciente.validCNS(this.camelObjectKeysToSnakeRecursive({pacienteId: this.model.paciente.id}))
                    .then(response => {

                        if (!response.data.additionals.cnsValido && response.data.additionals.avisaCnsTemporarioInvalido) {

                            this.showWarningNotification('Paciente com CNS inválido. Registro não permitido.')
                            this.model.paciente = null
                            return false
                        }

                        if (this.model.paciente.subclassificacaoId === this.enums.subclassificacao.TEMPORARIO
                            && response.data.additionals.avisaCnsTemporarioInvalido) {

                                const confirm = async () => {

                                    this.getArea()
                                    return false
                                }

                                const cancel = async () => {

                                    this.model.paciente = null
                                    return false
                                }

                                const msg = `Paciente com cadastro temporário. Deseja continuar?`

                                this.showConfirmation(msg, confirm, cancel)

                                return false
                        }

                        this.getArea()
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
        },
        'model.tipoAtendimentoId': function() {
            if(this.model.tipoAtendimentoId == this.enums.tipoAtendimento.VISITA_DOMICILIAR_POS_OBITO) {
                this.model.condicoes = []
            }
        }
    },
    props: {
        model: { type: Object, required: true },
        form: { type: Object, required: true }
    },
    mixins: [ repository, error, formats, stringCase ]
}
</script>